import React, { useState } from "react";
import { useMutation } from "react-query";
import { toast } from "react-toastify";
import api from "../../../utils/api";

const RECIPIENT_OPTIONS = [
  { key: "patients", label: "Patients", icon: "🧑‍🤝‍🧑" },
  { key: "doctors", label: "Doctors", icon: "👨‍⚕️" },
  { key: "labs", label: "Labs", icon: "🧪" },
];

export default function BroadcastMessage() {
  const [recipients, setRecipients] = useState([]);
  const [message, setMessage] = useState("");
  const [subject, setSubject] = useState("");
  const [channels, setChannels] = useState({ sms: false, email: false, whatsapp: false });

  const toggleRecipient = (key) =>
    setRecipients((prev) =>
      prev.includes(key) ? prev.filter((r) => r !== key) : [...prev, key]
    );

  const toggleChannel = (key) =>
    setChannels((prev) => ({ ...prev, [key]: !prev[key] }));

  const sendMutation = useMutation(
    () =>
      api.post("/admin/broadcast", {
        recipients,
        subject,
        message,
        channels,
      }),
    {
      onSuccess: () => {
        toast.success("Broadcast sent successfully!");
        setMessage("");
        setSubject("");
        setRecipients([]);
        setChannels({ sms: false, email: false, whatsapp: false });
      },
      onError: (err) => toast.error(err.message || "Failed to send"),
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    if (recipients.length === 0) {
      return toast.warn("Please select at least one recipient group");
    }
    if (!Object.values(channels).some(Boolean)) {
      return toast.warn("Please select at least one channel");
    }
    sendMutation.mutate();
  };

  return (
    <div className="max-w-2xl space-y-5">
      <div className="card">
        <h2 className="font-display font-semibold text-slate-800 text-lg mb-5">
          Compose Broadcast
        </h2>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Recipients */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wide">
              Send To
            </label>
            <div className="flex gap-3 flex-wrap">
              {RECIPIENT_OPTIONS.map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => toggleRecipient(opt.key)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-medium transition-colors ${
                    recipients.includes(opt.key)
                      ? "bg-primary-600 border-primary-600 text-white"
                      : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  <span>{opt.icon}</span>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Channels */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wide">
              Channels
            </label>
            <div className="flex gap-3 flex-wrap">
              {Object.keys(channels).map((ch) => (
                <button
                  key={ch}
                  type="button"
                  onClick={() => toggleChannel(ch)}
                  className={`px-4 py-2 rounded-xl border text-sm font-medium transition-colors capitalize ${
                    channels[ch]
                      ? "bg-secondary-500 border-secondary-500 text-white"
                      : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  {ch}
                </button>
              ))}
            </div>
          </div>

          {/* Subject */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">
              Subject
            </label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Message subject…"
              required
              className="input-field"
            />
          </div>

          {/* Message */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">
              Message
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={5}
              placeholder="Type your broadcast message here…"
              required
              className="input-field resize-none"
            />
            <p className="text-xs text-slate-400 mt-1 text-right">
              {message.length} characters
            </p>
          </div>

          <button
            type="submit"
            disabled={sendMutation.isLoading}
            className="btn-primary"
          >
            {sendMutation.isLoading ? "Sending…" : "📢 Send Broadcast"}
          </button>
        </form>
      </div>
    </div>
  );
}
